import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { getCatalogoDePadrones, getContentTypesPadrones } from 'api/catalogos';
import { getDatosDePagos, getDatosDePagosById, getMetodosDePago } from 'api/configuracion';
import { cancelacionVoucher } from 'api/netpay';
import { getCargo } from 'api/recaudacion/cargos';
import { getCortes } from 'api/recaudacion/cortes';
import {
  baseURI,
  getRecibo,
  getRecibos,
  getRecibosPDF,
  patchRecibo,
} from 'api/recaudacion/recibos';
import { getPadronesMap } from 'api/recaudacion/utils';
import { getUsuario } from 'api/usuarios/usuarios';
import FormSubmitControls from 'components/FormSubmitControls';
import InputSearch, { InputSearchPro } from 'components/InputSearch';
import NumericInput from 'components/NumericInput';
import Previewer from 'components/Previewer';
import Select from 'components/Select';
import CustomTable from 'components/Table';
import useCorte from 'hooks/useCorte';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SET_VISIBLE_MODAL_CORTE } from 'store/reducers/app';
import { SET_RECIBOS_CANCELADOS } from 'store/reducers/caja';
import getFormattedAddress from 'utils/address';
import { logger } from 'utils/debug';
import { ESTADOS_GLOBALES_MAP, isMutable } from 'utils/estadosGlobales';
import { DATE_FORMAT, formatReceived } from 'utils/formatValues';
import { getFileName } from 'utils/getFileName';
import { onError } from 'utils/handlers';
import { toUpper } from 'utils/normalizers';
import { getTipoDePadronPorContenType } from 'utils/padrones';
import padronToString, { getPadronKey } from 'utils/padronesToString';
import { ActionsNames } from 'utils/permission/index';
import {
  allModelsSearches,
  hasAdvanceSearchPermissions,
  hasPadronPermission,
  padronesModels,
} from 'utils/permission/models';
import currencyFormatter from 'utils/toCurrency';
import BusquedaRecibo from 'views/CatalogosDeRecaudacion/Recibo/BusquedaRecibo';
import ModalAgregarObservacion from 'views/CatalogosDeRecaudacion/Recibo/ModalAgregarObservacion';
import ModalAuthorize from 'views/CatalogosDeRecaudacion/Recibo/ModalAuthorize';
import ModalCancel from 'views/CatalogosDeRecaudacion/Recibo/ModalCancel';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import ModalReintegros from './ModalReintegros';

export const permissionConfig = {
  mainPermission: 'viewcrud_recibo',
  required: [
    'view_recibo',
    'view_datodepago',
  ],
  customActions: [
    {
      name: 'autorizar_recibo',
      title: 'Autorizar',
      permissions: ['autorizar_recibo', 'change_recibo'],
    },
    {
      name: 'cancelar_recibo',
      title: 'Cancelar',
      permissions: ['cancelar_recibo', 'change_recibo'],
    },
    {
      name: 'add_observacionesrecibo',
      title: 'Añadir Observaciones',
      permissions: ['add_observacionesrecibo', 'change_recibo'],
    },
    {
      name: 'desligar_factura',
      title: 'Desligar Factura',
      permissions: ['desligar_factura', 'change_recibo'],
    },
    {
      name: 'cancelar_recibo_convenio',
      title: 'Cancelar Recibo de Convenio',
      permissions: ['cancelar_recibo_convenio', 'change_recibo'],
    },
    {
      name: 'cancelar_pago',
      title: 'Cancelar Pago',
      permissions: [
        'cancelar_pago',
        'change_recibo',
        'view_usuario',
        'view_corte',
      ],
    },
  ],
  customSections: [
    {
      name: 'consulta_padrones',
      title: 'Consulta de padrones',
      models: padronesModels,
      action: ActionsNames.view,
    },
    {
      name: 'busquedas_avanzadas_padrones',
      title: 'Búsquedas avanzadas padrones',
      models: allModelsSearches,
      action: ActionsNames.view,
    },
  ],
};

// const netpayFeedback = ({ status_code, message: netPayMessage }) => {
//   const error = {
//     '00': 'success',
//     '02': 'warn',
//   };
//   const type = error[status_code] ?? 'info';
//   message[type](netPayMessage, 5);
// };

const Recibo = ({ permission }) => {
  const now = new Moment();
  const ref = useRef();
  const { corte } = useCorte(true);
  const { recibosCancelados } = useSelector(({ caja }) => caja);
  const user = useSelector(({ auth }) => auth.user);
  const dispatch = useDispatch();
  const timeoutRecibos = useRef();
  const { estadosGlobales, metodosDePago, padrones: _padrones } = useSelector((s) => s.catalogs);
  const padrones = _padrones.filter((p) => hasPadronPermission(p.id));
  const collapsedSidebar = useSelector(({ app }) => app.collapsedSidebar);
  const { padron_principal } = useSelector(({ auth }) => auth.entidad);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formCancel] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [visibleObservation, setVisibleObservation] = useState(false);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [visible, setVisible] = useState(false);
  const [base64, setBase64] = useState();
  const [dataSourceAbonos, setDataSourceAbonos] = useState([]);
  const [contentTypesPadron, setContentTypesPadron] = useState([]);
  const [hasAddress, setHasAddress] = useState(false);
  const [cuentaUnicaID, setCuentaUnicaID] = React.useState();
  const [params, setParams] = useState({});
  const [usersCajeros, setUsersCajeros] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [visiblePreviewPDF, setVisiblePreviewPDF] = useState(false);
  const [visiblePreviewXML, setVisiblePreviewXML] = useState(false);
  const [visibleAuth, setVisibleAuth] = useState(false);
  const [visibleCancel, setVisibleCancel] = useState(false);
  const [visibleReintegro, setVisibleReintegro] = useState(false);
  const [current, setCurrent] = useState(null);
  const [tipoDePadron, setTipoDePadron] = useState(padron_principal || 1);
  const [padronFound, setPadronFound] = useState(null);
  const [dataExterno, setDataExterno] = useState({ results: [] });
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [netPayLoading, setNetPayLoading] = useState(false);
  const [visibleGetRecibos, setVisibleGetRecibos] = useState(false);
  const [netPayLastPayment, setNetPayRecibos] = useState(null);
  const [datosDePago, setDatosDePago] = useState([]);
  const selected = data.results.find((e) => e.id === selectedRowKeys[0]);
  const _isMutable = isMutable(selected);
  const netpayRef = useRef();

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const [_contentTypes] = await Promise.all([
        getContentTypesPadrones(),
        getMetodosDePago(),
        getCatalogoDePadrones(),
      ]);
      if (mounted) {
        setContentTypesPadron(_contentTypes);
        form.setFieldsValue({ tipo_de_padron: padron_principal });
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fetchRecibos = async (page = 1, _params = null) => {
    setLoading(true);
    setCurrentPage(page);
    const search = { ..._params, ...params };
    const _recibos = await getRecibos({ page, ...search });
    if (_recibos?.results?.length) {
      setData(_recibos);
      const cajerosids = data.results.map((e) => e?.corte.usuario_cajero);
      const _cajeros = [...new Set(cajerosids)];
      const promises = _cajeros.map((e) => getUsuario(e, true));
      const _usersCajeros = (await Promise.allSettled(promises)).map((e) => e?.value);
      setUsersCajeros(_usersCajeros.map((e) => ({
        ...e,
        nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
      })));
    } else {
      message.warn('No se encontraron recibos');
      setData({ results: [] });
    }
    setLoading(false);
  };

  const onCancel = () => {
    setBase64();
    setCurrent(null);
    setVisible();
    form.resetFields();
    formInfo.resetFields();
    setPadronFound([]);
    setTipoDePadron(padron_principal);
    setParams({});
  };

  const rules = {
    folio: [{ required: true }],
  };

  const formatRecibo = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const recibo = await getRecibo(key);
    if (recibo) {
      const { padron, canal_de_pago, metodos_de_pago } = recibo;
      const _datosDePago = await Promise
        .all(metodos_de_pago.map((e) => getDatosDePagosById(e)));
      const cajero = recibo.corte?.usuario_cajero;
      const numEmpleado = cajero?.numero_de_empleado || cajero?.id;
      const nombreEmpleado = `${cajero?.first_name} ${cajero?.last_name} ${cajero?.second_last_name || ''}`;
      const _tipoDePadron = getTipoDePadronPorContenType(recibo.content_type_padron);
      const [, ...rest] = padronToString(padron, _tipoDePadron).split(' - ');
      const padron_nombre_completo = rest.join().replace(',', ' - ');
      const matchContentType = contentTypesPadron
        .find((e) => e.id === recibo.content_type_padron);
      const clave_padron = getPadronKey(padron, matchContentType.nivel);
      const formattedValues = formatReceived({
        clave_padron,
        folio: recibo.folio || recibo.id,
        fecha_de_creacion: recibo.fecha_de_creacion,
        tipo_padron: contentTypesPadron
          .find((e) => e.id === recibo.content_type_padron)?.model,
        cajero: cajero ? `${numEmpleado} - ${nombreEmpleado}` : null,
        padron_nombre_completo,
        estados_globales: estadosGlobales
          .find((e) => e.id === recibo.estados_globales)?.descripcion,
        importe_total: recibo.importe_total,
        canal: canal_de_pago.descripcion,
        direccion: getFormattedAddress(recibo.padron?.direccion
          || recibo.padron?.domicilio_fiscal, setHasAddress),
        observaciones: recibo.estados_globales === 5
          ? recibo.observacion_de_la_cancelacion : recibo.observaciones,
      });
      formInfo.setFieldsValue(formattedValues);
      setDataSourceAbonos(recibo.abonos);
      setDatosDePago(_datosDePago);
      setCurrent(recibo);
    }
    setLoading(false);
  };

  const onClickExpand = async () => {
    await formatRecibo();
    setVisible(true);
  };

  const onFinish = async () => {
    try {
      clearTimeout(timeoutRecibos.current);
      setLoading(true);
      await form.validateFields();
      const { folio } = form.getFieldsValue();
      await fetchRecibos(1, { folio, ...params });
      setLoading(false);
    } catch (err) {
      setData({ results: [] });
      onError(err, setLoading);
    }
  };

  const setTimeoutRecibos = (folio, ms) => {
    if (folio) {
      clearTimeout(timeoutRecibos.current);
      timeoutRecibos.current = setTimeout(() => onFinish(), ms);
    } else {
      clearTimeout(timeoutRecibos.current);
    }
  };

  const onValuesChange = (changed) => {
    const { folio } = changed;
    const changedProp = Object.keys(changed)?.shift();
    if (changedProp === 'folio') {
      setTimeoutRecibos(folio, 2000);
    }
  };

  const onFinishBusqueda = async (_data, _params) => {
    setData(_data);
    setParams(_params);
    const cajerosids = data.results.map((e) => e?.corte.usuario_cajero);
    const cajeros = [...new Set(cajerosids)];
    const promises = cajeros.map((e) => getUsuario(e));
    const _usersCajeros = await Promise.all(promises);
    setUsersCajeros(_usersCajeros.map((e) => ({
      ...e,
      nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
    })));
    setVisibleBusqueda(false);
    setSelectedRowKeys([]);
  };

  const columns = [
    {
      title: 'Concepto',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
    },
    {
      title: 'Adeudo',
      dataIndex: 'adeudo_base',
      key: 'adeudo_base',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      title: 'Actualizaciones',
      dataIndex: 'importe_actualizacion',
      key: 'importe_actualizacion',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      title: 'Recargos',
      dataIndex: 'importe_de_recargo',
      key: 'importe_de_recargo',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      title: 'Gastos',
      dataIndex: 'importe_de_gasto',
      key: 'importe_de_gasto',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      title: 'Desc. Adeudos',
      dataIndex: 'descuento_base',
      key: 'descuento_base',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      title: 'Desc. Recargos',
      dataIndex: 'descuento_recargo',
      key: 'descuento_recargo',
      align: 'right',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
  ];

  const columnsRecibo = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 150,
    },
    {
      titleText: 'Folio de Facturación',
      dataIndex: 'folio_de_facturacion',
      key: 'folio_de_facturacion',
      width: 220,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe_total',
      key: 'importe_total',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Cajero',
      dataIndex: 'corte',
      key: 'corte',
      width: 250,
      render: (val) => usersCajeros.find((e) => e.id === val?.usuario_cajero)?.nombre_completo,
    },
    {
      titleText: 'Canal de pago',
      dataIndex: 'canal_de_pago',
      key: 'canal_de_pago',
      width: 250,
      render: (val) => val?.descripcion,
    },
    {
      titleText: 'Folio de Tramite',
      dataIndex: 'tramite',
      key: 'tramite',
      width: 250,
      render: (val) => (val ? val?.folio : 'No pertenece a un tramite'),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const onClickPreview = async (keys = []) => {
    const _selectedRowKeys = keys.length ? keys : selectedRowKeys;
    clearTimeout(timeoutRecibos.current);
    setLoading(true);
    await formatRecibo();
    const results = await getRecibosPDF(_selectedRowKeys);
    if (results.success) {
      setBase64(results.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (recibosCancelados.length) {
      clearTimeout(netpayRef.current);
      netpayRef.current = null;
      setVisibleGetRecibos(false);
      setNetPayLoading(false);
      onClickPreview(recibosCancelados);
      dispatch({
        type: SET_RECIBOS_CANCELADOS,
        payload: [],
      });
    }
    // eslint-disable-next-line
  }, [recibosCancelados]);

  const handleOnRowClick = (record) => {
    setCurrent(formatReceived(record));
    setSelectedRowKeys([record?.id]);
    setCuentaUnicaID(record?.cuenta_unica);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAddObservation = () => {
    if (!_isMutable) {
      message.warn('No se pueden añadir observaciones a recibos cancelados/autorizados.');
      return;
    }
    setVisibleObservation(true);
  };

  const onClickReintegro = () => {
    setVisibleReintegro(true);
  };

  const paginationCallback = async (page = 1, restParams = {}) => {
    setSelectedRowKeys([]);
    const { folio } = form.getFieldsValue();
    await fetchRecibos(page, { folio, ...restParams, ...params });
  };

  const callbackStatus = (estadoGlobalId, values) => {
    const [key] = selectedRowKeys;
    const results = [...data.results];
    const idx = data.results.findIndex((e) => e.id === key);
    results[idx] = {
      ...results[idx],
      estados_globales: estadoGlobalId,
    };
    setData({ ...data, results });
    formInfo.setFieldsValue({
      ...formatReceived(values),
      corte: formatReceived(values.corte),
      importe_total: values.importe_total,
      estados_globales: estadosGlobales.find((eg) => eg.id === estadoGlobalId).descripcion,
    });
  };

  const onClickAuthorize = () => {
    if (!_isMutable) {
      message.warn('No se pueden Autorizar recibos cancelados/autorizados.');
      return;
    }
    setVisibleAuth(true);
  };

  const cancelSale = async (orderId, recibos = [], cortes = []) => {
    try {
      setNetPayLoading(true);
      await formCancel.validateFields();
      const { corte__cajero } = formCancel.getFieldsValue();
      if (!corte__cajero) {
        message.warn('Debe seleccionar un cajero con un corte abierto y con una terminal de NetPay.');
        setNetPayLoading(false);
        return false;
      }
      const { serial: serialNumber } = cortes.find((e) => e.id === corte__cajero)
        ?.terminal_de_netpay;
      const canceledSale = await cancelacionVoucher({
        serialNumber,
        orderId,
        traceability: {
          recibos: recibos.map((id) => ({ id })),
          observaciones: 'Cancelación por terminal',
          usuario: user.id,
        },
      });
      if (canceledSale) {
        ref.current = setTimeout(() => setVisibleGetRecibos(true), 60000);
        setNetPayRecibos(recibos);
      } else {
        setNetPayLoading(false);
      }
      return true;
    } catch (err) {
      logger(err);
    }
    return false;
  };

  // eslint-disable-next-line react/prop-types
  const OrderIdIndicator = ({ total = 0 }) => (
    <Tooltip title={(
      <Row>
        <Col span={24}>
          <strong>
            Verificación de OrderId y Monto
          </strong>
        </Col>
        <Col span={24} style={{ marginTop: 8 }}>
          <div>
            •
            <strong>OrderId:</strong>
            {' '}
            Debe coincidir exactamente con el que aparece en el voucher de NetPay
          </div>
          <div>
            •
            <strong>Monto:</strong>
            {' '}
            Debe ser
            {' '}
            {currencyFormatter(total)}
          </div>
        </Col>
        <Col span={24} style={{ marginTop: 8, fontSize: '12px' }}>
          Si los datos no coinciden, contacte al soporte técnico antes de procesar el pago.
        </Col>
      </Row>
    )}
    >
      <QuestionCircleOutlined />
    </Tooltip>
  );

  const modalAlertCancelVoucher = ({
    orderId = '',
    recibosId = [],
    _recibos = [],
    key = null,
    corte__cajero = corte?.id,
    cortes: _cortes = [],
  }) => {
    formCancel.setFieldsValue({ corte__cajero });
    const ttl = _recibos.reduce((prev, curr) => prev + curr.importe_total, 0);
    Modal.confirm({
      okButtonProps: {
        loading: netPayLoading,
        icon: <CloseCircleOutlined />,
        type: 'danger',
      },
      cancelButtonProps: {
        icon: <ArrowLeftOutlined />,
      },
      centered: true,
      autoFocusButton: null,
      width: '90vw',
      cancelText: 'Regresar',
      okText: 'Continuar',
      title: (
        <>
          <Typography.Title level={4} style={{ marginBottom: 12, color: '#d4380d' }}>
            Cancelación de Transacción NetPay
          </Typography.Title>

          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ fontSize: '15px' }}>
              ¿Confirma la cancelación de la transacción con OrderId:
              {' '}
              {orderId}
              ?
            </Typography.Text>
            <OrderIdIndicator total={ttl} />
          </div>

          <Alert
            type="error"
            style={{ marginBottom: 16 }}
            message="Acción Irreversible"
            description="Esta operación no se puede deshacer. Una vez cancelada, la transacción no podrá ser procesada nuevamente."
          />

          <Alert
            type="warning"
            message={(
              <Typography.Text strong style={{ color: '#389e0d', display: 'block', marginBottom: 8 }}>
                Verificaciones Requeridas:
              </Typography.Text>
            )}
            description={(
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li style={{ marginBottom: 4 }}>
                  <strong>OrderId:</strong>
                  {' '}
                  Debe coincidir exactamente con el voucher de NetPay
                </li>
                <li style={{ marginBottom: 4 }}>
                  <strong>Monto:</strong>
                  {' '}
                  Debe ser
                  {' '}
                  {currencyFormatter(ttl)}
                </li>
                <li>
                  <strong>Fecha:</strong>
                  {' '}
                  Solo se puede cancelar el mismo día de emisión
                </li>
                <li>
                  <strong>Terminal:</strong>
                  {' '}
                  Debe ser la misma terminal de NetPay y estar encendida
                </li>
              </ul>
            )}
          />
        </>
      ),
      onCancel: () => Modal.destroyAll(),
      onOk: async () => {
        Modal.destroyAll();
        await cancelSale(orderId, recibosId, _cortes);
      },
      content: (
        <>
          {_recibos.length > 1 && (
            <>
              <br />
              <Row style={{ width: '100%' }} justify="end">
                <Col span={24}>
                  <Typography.Text>
                    {`Ademas del recibo actual (${current?.folio}).`}
                  </Typography.Text>
                </Col>
                <Col span={24}>
                  <Typography.Text>
                    Los siguientes recibos también serán cancelados debido a que se cobraron en la
                    misma transacción de NetPay.
                  </Typography.Text>
                </Col>
                <Col>
                  <Typography.Text>
                    {`Monto total de todos los recibos ${currencyFormatter(ttl || 0)}`}
                  </Typography.Text>
                </Col>
              </Row>
              <br />
              <Row>
                <CustomTable
                  cols={columnsRecibo}
                  data={_recibos.filter((e) => e.id !== key)}
                  rowKey="id"
                  noControls
                />
              </Row>
            </>
          )}
          <Form
            form={formCancel}
            layout="vertical"
            name="formCancel"
          >
            <Row>
              <Col xs={24} sm={24} md={16}>
                <Form.Item
                  label="Seleccione al cajero con el cual desea hacer la cancelación"
                  name="corte__cajero"
                  rules={rules.required}
                  tooltip={(
                    <span>
                      Si el campo aparece deshabilitado se debe a que su corte es el único
                      disponible para realizar la cancelación.
                    </span>
                  )}
                >
                  <Select
                    dataSource={_cortes}
                    render={(e) => `${e?.usuario_cajero?.nombre_completo}`}
                    disabled={_cortes.length === 1 && _cortes[0]?.id === corte__cajero}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </>
      ),
    });
  };

  const onClickCancelNetpay = async (record) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _cortes = await getCortes({
      apertura_after: now.format(DATE_FORMAT),
      unidad_de_recaudacion: user.unidad_de_recaudacion,
    });
    const _cortesDisponibles = _cortes.filter((c) => c.terminal_de_netpay && !c.cierre);
    const _cajeros = _cortesDisponibles.map((e) => e.usuario_cajero);
    const alreadyHasCorte = _cortes.filter((c) => c.cierre)
      .map((c) => c.usuario_cajero).includes(user.id);
    let _cortesAvialable = [];
    if (_cortesDisponibles.every((c) => typeof c.usuario_cajero === 'number')) {
      const promises = [...new Set(_cajeros)].map((u) => getUsuario(u));
      const _usuarios = (await Promise.allSettled(promises)).map((e) => e?.value);
      const cortesDisponibles = _cortesDisponibles.map((c) => ({
        ...c,
        usuario_cajero: _usuarios.map((e) => ({
          ...e,
          nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
        }))
          .find((u) => u.id === c.usuario_cajero),
      })).filter((c) => c.terminal_de_netpay);
      _cortesAvialable = cortesDisponibles;
    } else {
      _cortesAvialable = _cajeros;
    }
    if (!_cortesAvialable.length && !corte) {
      setLoading(false);
      dispatch({
        type: SET_VISIBLE_MODAL_CORTE,
        payload: true,
      });
      message.warn(`No hay cajeros disponibles para realizar la cancelación. Si desea cancelar la transacción de NetPay, por favor ${alreadyHasCorte ? 'vuelva a abrir el corte de hoy' : ' abra un corte nuevo'}.`, 30);
      return;
    }
    const { numero_de_transaccion } = record;
    const _everyPaymentData = await getDatosDePagos({ numero_de_transaccion });
    const responses = await Promise
      .all(_everyPaymentData.map((pd) => getRecibos({ metodos_de_pago: pd.id })));
    const _recibos = responses.map((e) => e.results)
      .reduce((accum, curr) => [...accum, ...curr], []);
    const orderId = record.numero_de_transaccion;
    const recibosId = _recibos.map((e) => e.id);
    setLoading(false);
    modalAlertCancelVoucher({
      orderId,
      recibosId,
      _recibos,
      key,
      cortes: _cortesAvialable,
    });
  };

  const onClickCancel = async () => {
    try {
      if (!_isMutable) {
        message.warn('No se pueden Cancelar recibos cancelados/autorizados.');
        return;
      }
      const [key] = selectedRowKeys;
      const match = data.results.find((e) => e.id === key);
      const cargoId = match?.abonos?.[0]?.cargo?.id;
      setLoading(true);
      const cargo = await getCargo(cargoId);

      if (cargo?.convenios
        && cargo?.convenios?.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO) {
        message.warn('No se puede cancelar este recibo, pertenece a un convenio.');
        return;
      }

      // se obtiene los datos de pago para saber si se pagó con NetPay
      const _datosDePago = await Promise
        .all(match.metodos_de_pago.map((e) => getDatosDePagosById(e)));

      const netpayData = _datosDePago.find((e) => e.numero_de_transaccion);

      // si se pagó con NetPay, se llama a la función para cancelar la transacción
      if (netpayData?.numero_de_transaccion
      && (typeof netpayData?.numero_de_transaccion === 'string'
      && netpayData?.numero_de_transaccion?.includes('-'))) {
        if (/cancelado/i.test(current?.estados_globales)) {
          message.warn('El recibo ya fue cancelado.');
          return;
        }
        if (!permission.cancelar_pago) {
          message.warn('No tiene permisos para realizar esta acción.');
          return;
        }
        if (current?.fecha_de_creacion && now.format(DATE_FORMAT)
          .localeCompare(Moment(current?.fecha_de_creacion).format(DATE_FORMAT)) !== 0) {
          message.warn('Ya pasó más de un día desde su creación.');
          return;
        }
        await onClickCancelNetpay(netpayData);
        return;
      }

      setVisibleCancel(true);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const cancelarFeactura = async () => {
    const [key] = selectedRowKeys;
    const match = data.results.find((e) => e.id === key);
    if ([4, 5].includes(match?.estados_globales)) {
      message.warn('No se pueden realizar cambios a recibos cancelados/autorizados.');
      return;
    }
    if (!match?.xml_archivo) {
      message.warn('Este recibo no ha sido facturado.');
      return;
    }
    setLoading(true);
    const result = await patchRecibo(
      match.id,
      { pdf_de_rfc: null, xml_archivo: null, folio_de_facturacion: null },
      'La factura fue desligada exitosamente.',
    );
    if (!result?.pdf_de_rfc && !result?.xml_archivo) {
      const results = data.results.map((e) => (e.id === key ? ({ ...result }) : e));
      setData({ ...data, results });
    }
    setLoading(false);
  };

  const displayTitle = (id) => padrones.find((e) => e.id === id)?.descripcion;

  const formatPadrones = async (_padron) => {
    setLoading(true);
    const nombre_completo = padronToString(_padron, tipoDePadron);
    setPadronFound({ ..._padron, nombre_completo });
    form.setFieldsValue({ padron_id: _padron.id });
    const _params = {
      padron: tipoDePadron,
      padron_id: _padron.id,
    };
    setParams(_params);
    await fetchRecibos(1, _params);
    setLoading(false);
  };

  const handleSearchPadron = async () => {
    try {
      setLoading(true);
      setSelectedRowKeys([]);
      const { q } = form.getFieldsValue();
      const found = await getPadronesMap[tipoDePadron]({ q });
      if (found?.length === 1 || found?.results.length === 1) {
        const [first] = found?.results || found;
        await formatPadrones(first);
      } else if (found.results.length > 1) {
        setDataExterno(found);
        setVisibleBusquedaPadrones(true);
        setPadronFound([]);
      } else {
        message.warn('No se encontró ningún padron relacionado.');
        setPadronFound([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const resetAndValidate = () => {
    setPadronFound([]);
    form.resetFields(['q']);
    setTipoDePadron(padron_principal);
    setData({ results: [], count: 0 });
    form.setFieldsValue({ tipo_de_padron: padron_principal });
    setParams({});
  };

  const getReciboNetPay = async () => {
    setLoading(true);
    const recibos = await getRecibosPDF(netPayLastPayment);
    if (recibos.success) {
      setNetPayLoading(false);
      setLoading(false);
      setNetPayRecibos(null);
      message.info('Recibos encontrados');
      setBase64(recibos.base64);
      setVisiblePreview(true);
      clearTimeout(ref.current);
      ref.current = null;
      setVisibleGetRecibos(false);
    } else {
      setLoading(false);
    }
  };

  const cancelButtonNetpay = (_, record) => {
    const errors = [];
    if (!record?.numero_de_transaccion
      || (typeof record?.numero_de_transaccion === 'string'
      && !record?.numero_de_transaccion?.includes('-'))) {
      errors.push(<strong>El recibo no fue pagado con NetPay.</strong>);
    }
    if (/cancelado/i.test(current?.estados_globales)) {
      errors.push('El recibo ya fue cancelado.');
    }
    if (!permission.cancelar_pago) {
      errors.push('No tiene permisos para realizar esta acción.');
    }
    if (current?.fecha_de_creacion && now.format(DATE_FORMAT)
      .localeCompare(Moment(current?.fecha_de_creacion).format(DATE_FORMAT)) !== 0) {
      errors.push('Ya pasó más de un día desde su creación.');
    }

    const title = errors.length > 1 ? 'No se puede cancelar la transacción por el siguiente motivo'
      : 'No se puede cancelar la transacción por los siguientes motivos';

    if (errors.length) {
      return (
        <Tooltip title={(
          <>
            {title}
            <ul>
              {errors.map((e) => <li>{e}</li>)}
            </ul>
          </>
        )}
        >
          <Button
            type="link"
            icon={<DeleteOutlined />}
            disabled
          >
            Cancelar pago
          </Button>
        </Tooltip>
      );
    }

    return (
      <Button
        type="link"
        className="danger"
        onClick={() => onClickCancelNetpay(record)}
        icon={<DeleteOutlined />}
      >
        Cancelar pago
      </Button>
    );
  };

  const NetPayLoading = () => (
    <>
      Esperando respuesta de terminal
      <br />
      {visibleGetRecibos && (
        <Button
          onClick={getReciboNetPay}
          style={{
            marginTop: 10,
          }}
        >
          Recuperar recibos
          <Tooltip title="Solo usar en caso de finalizar la transacción y no recibir el recibo">
            <QuestionCircleOutlined />
          </Tooltip>
        </Button>
      )}
      <br />
      <Button
        onClick={() => {
          setNetPayLoading(false);
          clearTimeout(ref.current);
          ref.current = null;
          setVisibleGetRecibos(false);
        }}
        style={{
          marginTop: 10,
        }}
      >
        Cancelar
      </Button>
    </>
  );

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip={netPayLoading ? <NetPayLoading /> : 'Cargando...'}
        spinning={loading || netPayLoading}
      >
        {!visible ? (
          <>
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              onFinish={() => onFinish()}
              onValuesChange={onValuesChange}
              initialValues={{ tipo_de_padron: padron_principal }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={collapsedSidebar ? 12 : 16} lg={8}>
                  <InputSearch
                    name="folio"
                    label="Folio"
                    rules={rules.folio}
                    onPressEnter={() => {
                      clearTimeout(timeoutRecibos.current);
                      onFinish();
                    }}
                    normalizer={toUpper}
                    onClickAdvanced={() => setVisibleBusqueda(true)}
                  />
                </Col>
                <InputSearchPro
                  prefix={{
                    style: { width: '100%' },
                    name: 'tipo_de_padron',
                    label: 'padron',
                    dataSource: padrones.filter((e) => e.id !== 5),
                    onChange: (e) => setTipoDePadron(e),
                  }}
                  name="q"
                  onClickAdvanced={hasAdvanceSearchPermissions(tipoDePadron)
                    ? () => setVisibleBusquedaPadrones(true) : null}
                  label={displayTitle(tipoDePadron)}
                  inputProps={{
                    onPressEnter: () => handleSearchPadron(),
                  }}
                  breakPoints={{
                    xs: 24,
                    sm: 24,
                    md: collapsedSidebar ? 12 : 16,
                  }}
                  resultProps={{
                    breakPoints: {
                      xs: 24,
                      sm: 24,
                      md: collapsedSidebar ? 12 : 16,
                    },
                    rules: rules.required,
                    name: 'padron_id',
                    labelProp: 'nombre_completo',
                    label: displayTitle(tipoDePadron),
                    dataSource: padronFound,
                    onClear: resetAndValidate,
                  }}
                />
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
            <CustomTable
              cols={columnsRecibo}
              data={data?.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{ onClickExpand }}
              allowExpand
              allowSearch={false}
              allowSort={false}
              rowKey="id"
              count={data.count}
              customActions={[
                {
                  onClick: () => onClickAuthorize(),
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  hasPermission: permission.autorizar_recibo,
                },
                {
                  onClick: () => onClickCancel(),
                  icon: <CloseCircleOutlined />,
                  text: 'Cancelar',
                  hasPermission: permission.cancelar_recibo,
                },
                {
                  onClick: onClickAddObservation,
                  icon: <PlusCircleOutlined />,
                  text: 'Observaciones',
                  hasPermission: permission.add_observacionesrecibo,
                },
                {
                  onClick: onClickReintegro,
                  icon: <PlusCircleOutlined />,
                  text: 'Reintegro',
                  hasPermission: permission.add_observacionesrecibo,
                },
                {
                  onClick: onClickPreview,
                  icon: <EyeOutlined />,
                  text: 'Ver Comprobante de pago',
                },
                {
                  onClick: cancelarFeactura,
                  icon: <CloseOutlined />,
                  text: 'Desligar Factura',
                  hasPermission: permission.desligar_factura,
                },
              ]}
              pagination={false}
              paginationCallback={paginationCallback}
              currentPage={currentPage}
            />
          </>
        ) : (
          <Card
            className="form-container"
            title="Información Recibo"
            extra={(<FormSubmitControls onCancel={onCancel} />)}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={formInfo}
              className="no-margin"
            >
              <Row gutter={10}>
                {/* info recibo */}
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="folio"
                    label="Folio"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="tipo_padron"
                    label="Tipo de padrón"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="fecha_de_creacion"
                    label="Fecha"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      placeholder=""
                      allowClear
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="cajero"
                    label="Cajero"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                {/* info padron */}
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave_padron"
                    label="Clave"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={16}>
                  <Form.Item
                    name="padron_nombre_completo"
                    label="Nombre"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col span={hasAddress ? 24 : 0}>
                  <Form.Item
                    name="direccion"
                    label="Domicilio"
                  >
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 2 }} disabled allowClear />
                  </Form.Item>
                </Col>
                <Divider orientation="left"><Typography.Text>Datos de Pago</Typography.Text></Divider>
                <CustomTable
                  noControls
                  allowSort={false}
                  data={datosDePago}
                  cols={[
                    {
                      titleText: 'Método de pago',
                      dataIndex: 'metodo_de_pago',
                      key: 'metodo_de_pago',
                      width: 200,
                      render: (e) => metodosDePago.find((m) => m.id === e)?.concepto,
                    },
                    {
                      titleText: 'Importe pagado con este método',
                      dataIndex: 'importe',
                      key: 'importe',
                      width: 200,
                      align: 'right',
                      render: (e) => currencyFormatter(e || 0),
                    },
                    {
                      titleText: '',
                      dataIndex: '_',
                      key: '_',
                      width: 200,
                      align: 'right',
                      render: cancelButtonNetpay,
                    },
                  ]}
                  rowKey="id"
                />
                <Divider orientation="left"><Typography.Text>Conceptos de Pago</Typography.Text></Divider>
                <Table
                  size="small"
                  columns={columns}
                  dataSource={dataSourceAbonos}
                  pagination={{
                    position: ['bottomLeft'],
                  }}
                  scroll={{
                    y: 300,
                  }}
                  rowKey="id"
                  summary={(_data) => {
                    const totalsByKey = {};
                    columns.forEach((col) => {
                      const key = col.dataIndex;
                      if (!key) return;
                      totalsByKey[key] = _data
                        .reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);
                    });

                    return (
                      <Table.Summary.Row className="align-right">
                        {columns.map((col, idx) => {
                          const cellKey = col.key || col.dataIndex || idx;
                          let content = null;
                          if (idx === 0) {
                            content = <b>Totales:</b>;
                          } else if (col.align === 'right' && col.dataIndex) {
                            content = currencyFormatter(totalsByKey[col.dataIndex] || 0);
                          }
                          return (
                            <Table.Summary.Cell key={cellKey}>
                              {content}
                            </Table.Summary.Cell>
                          );
                        })}
                      </Table.Summary.Row>
                    );
                  }}
                />
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="canal"
                    label="Canal"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="estados_globales"
                    label="Estado"
                  >
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={onClickPreview}
                    block
                    style={{ marginTop: 21 }}
                  >
                    <EyeOutlined />
                    <Typography.Text>
                      Ver Comprobante de pago
                    </Typography.Text>
                  </Button>
                </Col>
                {/* <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={() => {
                      if (current?.pdf_de_rfc) {
                        setVisiblePreviewPDF(true);
                      } else {
                        message.warn('El recibo actual no ha sido facturado.');
                      }
                    }}
                    block
                    style={{ marginTop: 21 }}
                  >
                    <EyeOutlined />
                    <Typography.Text>
                      Ver Factura
                    </Typography.Text>
                  </Button>
                </Col> */}
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={() => {
                      if (current?.xml_archivo) {
                        const oldURL = current.xml_archivo;
                        // const baseURL = 'https://demosigob.obs.la-north-2.myhuaweicloud.com/demosigob/media/';
                        // eslint-disable-next-line max-len
                        // const updatedURL = oldURL.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, baseURL);
                        const baseURL = 'https://mun-oaxaca.obs.la-north-2.myhuaweicloud.com/mun-oaxaca/media/';
                        const updatedURL = oldURL.replace(/^http:\/\/obs-mun-oaxaca\.migob\.digital\/mun-oaxaca\/media\//, baseURL);
                        const croppedURL = `${updatedURL.split('.xml')[0]}.xml`;
                        window.open(croppedURL);
                      } else {
                        message.warn('El recibo actual no ha sido facturado.');
                      }
                    }}
                    block
                    style={{ marginTop: 21 }}
                  >
                    <EyeOutlined />
                    <Typography.Text>
                      Ver Factura XML
                    </Typography.Text>
                  </Button>
                </Col>
                <Col xs={0} sm={0} md={18} />
                <Col xs={24} sm={24} md={6}>
                  <NumericInput
                    disabled
                    name="importe_total"
                    label="Total"
                    className="align-right"
                    currency
                  />
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                  >
                    <Input.TextArea autoSize={{ minRows: 4 }} disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        )}
      </Spin>
      <BusquedaRecibo
        onFinishBusqueda={onFinishBusqueda}
        visible={visibleBusqueda}
        setVisible={setVisibleBusqueda}
      />
      <ModalAgregarObservacion
        visible={visibleObservation}
        setVisible={setVisibleObservation}
        selectedRowKeys={selectedRowKeys}
        callback={(record) => {
          const results = [...data.results];
          const idx = data.results.findIndex((e) => e.id === selectedRowKeys[0]);
          results[idx].observaciones = record.observaciones;
          setData({ ...data, results });
        }}
      />
      <Previewer
        visible={visiblePreviewPDF}
        setVisible={setVisiblePreviewPDF}
        iFrameUrl={current?.pdf_de_rfc}
      />
      <Previewer
        visible={visiblePreviewXML}
        setVisible={setVisiblePreviewXML}
        iFrameUrl={current?.xml_archivo}
      />
      <Previewer
        allowSend
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
        id={selectedRowKeys[0]}
        baseURL={baseURI}
        cuentaUnicaID={cuentaUnicaID}
        allowDownload
        customFileNameFormat={() => {
          const fileName = getFileName({
            data: current,
            tipoDeDocumento: 'Recibo',
          });
          return fileName;
        }}
      />
      <ModalAuthorize
        visible={visibleAuth}
        setVisible={setVisibleAuth}
        reciboId={selectedRowKeys[0]}
        callback={callbackStatus}
      />
      <ModalCancel
        visible={visibleCancel}
        setVisible={setVisibleCancel}
        reciboId={selectedRowKeys[0]}
        callback={callbackStatus}
      />
      <ModalReintegros
        visible={visibleReintegro}
        setVisible={setVisibleReintegro}
        reciboId={selectedRowKeys[0]}
        // callback={callbackStatus}
      />
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadrones}
        setVisible={setVisibleBusquedaPadrones}
        callback={formatPadrones}
        dataExterno={dataExterno}
        setDataExterno={setDataExterno}
        tipoDePadron={tipoDePadron}
        allowAdd={false}
      />
    </Row>
  );
};

Recibo.propTypes = {
  permission: PropTypes.shape({
    autorizar_recibo: PropTypes.bool,
    cancelar_recibo: PropTypes.bool,
    add_observacionesrecibo: PropTypes.bool,
    desligar_factura: PropTypes.bool,
    cancelar_pago: PropTypes.bool,
    cancelar_recibo_convenio: PropTypes.bool,
  }).isRequired,
};

export default Recibo;
